// SMS-Eingang und Reiter Messages (Runde 6, Agent 6D): Typen und Client nach crm/API.md, Abschnitt 21.2. Vorarbeit: die Formen
// stehen, die Routen antworten bis zum Bau von 6D mit leeren Listen bzw. 501 `nicht_umgesetzt`. 6D erweitert diese Datei nur additiv.
import { http } from "./client";
import type { Liste, ListenParameter } from "./types";

export type SmsStatus = "empfangen" | "geplant" | "sendet" | "gesendet" | "zugestellt" | "fehlgeschlagen" | "abgebrochen";
export type SmsRichtung = "eingehend" | "ausgehend";

export interface SmsZeile {
  id: string;
  /** Aktivität `SMS` im Verlauf (leer bei geplanten) */
  activity_id: string | null;
  richtung: SmsRichtung;
  status: SmsStatus;
  lead_id: string | null;
  lead_name: string | null;
  contact_id: string | null;
  contact_name: string | null;
  /** zuständig (eingehend) bzw. Absender (ausgehend) */
  user_id: string | null;
  lokale_nummer: string | null;
  entfernte_nummer: string | null;
  text: string | null;
  geplant_at: string | null;
  gesendet_at: string | null;
  zugestellt_at: string | null;
  fehler_code: string | null;
  fehler_text: string | null;
  gelesen: boolean;
  date_created: string | null;
  // additiv (Runde 6, 6D)
  /** erledigt (Haken in der Inbox); bei fehlgeschlagenen Sendungen: Fehler zur Kenntnis genommen */
  erledigt: boolean;
  /** Aufgabe `incoming_sms` in der Inbox, die mit der SMS erledigt wird */
  task_id: string | null;
  /** geplant, Zeitpunkt vorbei, noch nicht gesendet (Versand nicht freigegeben oder außerhalb 8 bis 21 Uhr) */
  ueberfaellig: boolean;
  /** geplant und im CRM entstanden: lässt sich aufheben */
  kann_aufheben: boolean;
  /** Nummer lesbar formatiert */
  entfernte_anzeige: string | null;
  nutzer_name: string | null;
}

export interface SmsZaehler {
  ungelesen: number;
  fehlgeschlagen: number;
  geplant: number;
  /** eingehend, nicht erledigt */
  offen?: number;
  /** Versand geplanter SMS ist eingeschaltet */
  freigabe?: boolean;
}

export interface SmsParameter extends ListenParameter {
  richtung?: SmsRichtung;
  status?: SmsStatus[];
  nur_ungelesen?: boolean;
  lead_id?: string;
  /** zuständig bzw. Absender; leer = alle sichtbaren */
  user_id?: string;
  q?: string;
  /** eingehend und nicht erledigt, dazu fehlgeschlagene Sendungen */
  nur_offen?: boolean;
  /** Verlauf mit dieser Nummer */
  nummer?: string;
}

/** Ergebnis je SMS bei `POST /api/sms/nachrichten/erledigt` */
export interface SmsMehrereAntwort {
  ergebnisse: { id: string; ok: boolean; sms?: SmsZeile; aufgabe_fehler?: string | null; fehler?: { code: string; message: string } }[];
  ok: number;
  fehler: number;
}

export interface SmsPlanenEingabe {
  lead_id: string;
  contact_id?: string;
  /** Zielnummer */
  an: string;
  text: string;
  /** ISO-Zeitpunkt */
  geplant_at: string;
  vorlage_id?: string;
  /** eigene Nummer; leer = Standard des Nutzers */
  absender?: string;
  /** bestätigte Hinweise (Codes) wie beim Senden */
  bestaetigt?: string[];
}

export const smsEingangApi = {
  zaehler: () => http.get<SmsZaehler>("/api/sms/zaehler"),
  liste: (p: SmsParameter = {}, signal?: AbortSignal) =>
    http.get<Liste<SmsZeile>>(
      "/api/sms/nachrichten",
      {
        limit: p.limit, cursor: p.cursor, richtung: p.richtung, status: p.status, nur_ungelesen: p.nur_ungelesen, lead_id: p.lead_id,
        user_id: p.user_id, q: p.q, nur_offen: p.nur_offen, nummer: p.nummer,
      },
      signal,
    ),
  eine: (id: string) => http.get<{ sms: SmsZeile }>(`/api/sms/nachrichten/${encodeURIComponent(id)}`),
  gelesen: (id: string, gelesen = true) => http.post<{ sms: SmsZeile }>(`/api/sms/nachrichten/${encodeURIComponent(id)}/gelesen`, { gelesen }),
  /** ohne `ids`: alle ungelesenen des Nutzers */
  alleGelesen: (ids?: string[]) => http.post<{ anzahl: number }>("/api/sms/nachrichten/gelesen", ids ? { ids } : {}),
  /** Erledigen (Haken); schließt die Aufgabe in der Inbox mit. `aufgabe_fehler`: die Aufgabe blieb offen, Grund. */
  erledigt: (id: string, erledigt = true) =>
    http.post<{ sms: SmsZeile; aufgabe_fehler: string | null }>(`/api/sms/nachrichten/${encodeURIComponent(id)}/erledigt`, { erledigt }),
  mehrereErledigt: (ids: string[], erledigt = true) => http.post<SmsMehrereAntwort>("/api/sms/nachrichten/erledigt", { ids, erledigt }),
  /** `freigabe`: ob der Versand geplanter SMS eingeschaltet ist (sonst bleiben sie liegen) */
  geplant: () => http.get<{ data: SmsZeile[]; freigabe?: boolean }>("/api/sms/geplant"),
  planen: (e: SmsPlanenEingabe) => http.post<{ sms: SmsZeile }>("/api/sms/geplant", e),
  planAufheben: (id: string) => http.del<void>(`/api/sms/geplant/${encodeURIComponent(id)}`),
};
